import React, {useState} from "react";
import {FixedSizeList} from "react-window";
import {useTranslation} from "react-i18next";
import {Alert, Box, Grid, IconButton, ListItem, Stack, Tooltip} from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import CheckIcon from "@mui/icons-material/Check";
import {Field} from "../../types/domain/field";
import {useCrops} from "../../hooks/cropsHooks";
import {useSoils} from "../../hooks/soilsHooks";
import {useIrrigations} from "../../hooks/irrigationsHooks";
import {FieldCreateDialog} from "../dialogs/FieldCreateDialog";
import {FieldsTableHeader} from "./FieldsTableHeader";
import {useFieldManagement} from "../../hooks/fieldManagementHooks";
import {swallowException} from "../../utils/appUtils";
import {getResourceErrorMessage, isResourceFailed} from "../../utils/resourceUtils";
import {FieldStatus} from "../../types/domain/fieldStatus";
import {getLatestCropId} from "../../utils/fieldUtils";
import {FieldTableCell} from "./FieldTableCell";
import {FieldEditDialog} from "../dialogs/FieldEditDialog";
import {EditIcon} from "../icons/EditIcon";
import {useMeasureSystemUtils} from "../../hooks/measureSystemUtilsHooks";
import {getFieldAreaFormatted} from "../../utils/measureSystemUtils";

interface FieldCompProps {
    orderId?: string;
    farmId?: string;
    companyId: string;
    isAdmin: boolean;
    fields: Field[];
    refresh: () => void;
}

interface RowProps {
    index: number;
    style: React.CSSProperties;
}

export const FieldsTable: React.FC<FieldCompProps> = ({companyId, isAdmin, refresh, fields}) => {
    const headerBreakpoints = [4, 1, 2, 2, 2, 3];
    const {t} = useTranslation();
    const msUtils = useMeasureSystemUtils();
    const [fieldEditDialogOpened, setFieldEditDialogOpened] = useState<boolean>(false);
    const [selectedField, setSelectedField] = useState<Field | null>(null);
    const {cropsById} = useCrops();
    const {soilsById} = useSoils();
    const {irrigationsById} = useIrrigations();
    const fieldManagement = useFieldManagement();

    const finalizeField = (field: Field) => {
        fieldManagement
            .finalize(companyId, field.id)
            .then(() => {
                refresh();
            })
            .catch(swallowException);
    };

    const handleCloseAlert = () => {
        fieldManagement.reset();
    };

    const onFieldEditDialogClose = (isSuccessful: boolean) => {
        setFieldEditDialogOpened(false);
        if (isSuccessful) {
            refresh();
        }
    };

    const FieldRow: React.FC<RowProps> = ({index, style}) => {
        const field = fields[index];
        return (
            <ListItem style={style} key={index} component="div" disablePadding>
                <Grid container key={`cont-${index}`} direction="row" columns={14}>
                    <Grid item xs={headerBreakpoints[0]} zeroMinWidth>
                        <FieldTableCell color="primary">{field.name}</FieldTableCell>
                    </Grid>
                    <Grid item xs={headerBreakpoints[1]}>
                        <FieldTableCell>
                            <CheckIcon color={getFinalColor(field)} />
                        </FieldTableCell>
                    </Grid>
                    <Grid item xs={headerBreakpoints[2]}>
                        <FieldTableCell>
                            {getLatestCropId(field) ? cropsById[getLatestCropId(field)!].name : ""}
                        </FieldTableCell>
                    </Grid>
                    <Grid item xs={headerBreakpoints[3]}>
                        <FieldTableCell>{soilsById[field.soilId].name}</FieldTableCell>
                    </Grid>
                    <Grid item xs={headerBreakpoints[4]}>
                        <FieldTableCell>{irrigationsById[field.irrigationTypeId].name}</FieldTableCell>
                    </Grid>
                    <Grid item xs={headerBreakpoints[5]}>
                        <Stack direction="row" justifyContent="space-between">
                            <FieldTableCell>
                                {getFieldAreaFormatted(field, msUtils)} {msUtils.getAreaUnits()}.
                            </FieldTableCell>
                            <Stack direction="row" justifyContent="flex-end">
                                {isAdmin && (
                                    <Tooltip title={t("fields.fields_table.finalize")}>
                                        <span>
                                            <IconButton
                                                disabled={field.status === FieldStatus.FINAL}
                                                color="primary"
                                                onClick={() => {
                                                    finalizeField(field);
                                                }}
                                                sx={{
                                                    paddingTop: "0px",
                                                    paddingBottom: "0px",
                                                }}
                                            >
                                                <DoneIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}

                                <Tooltip title={t("fields.fields_table.edit")}>
                                    <IconButton
                                        color="primary"
                                        onClick={() => {
                                            setSelectedField(field);
                                            setFieldEditDialogOpened(true);
                                        }}
                                        sx={{
                                            paddingTop: "0px",
                                            paddingBottom: "0px",
                                        }}
                                    >
                                        <EditIcon sx={{stroke: "#6476CB", width: "1.1rem", height: "1.1rem"}} />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Stack>
                    </Grid>
                </Grid>
            </ListItem>
        );
    };

    const fieldHeaderItems = [
        t("fields.fields_table.header_field"),
        t("fields.fields_table.header_status"),
        t("fields.fields_table.header_crop"),
        t("fields.fields_table.header_soil"),
        t("fields.fields_table.header_irrigation"),
        t("fields.fields_table.header_area"),
    ];

    const leftPadding = "5.2rem";
    return (
        <Box>
            {isResourceFailed(fieldManagement.resource) && (
                <Alert severity="error" onClose={handleCloseAlert}>
                    {getResourceErrorMessage(fieldManagement.resource)}
                </Alert>
            )}
            <FieldsTableHeader
                items={fieldHeaderItems}
                columns={14}
                breakpoints={headerBreakpoints}
                sx={{paddingLeft: "5.2rem"}}
            ></FieldsTableHeader>
            <Box bgcolor="white" p={1} sx={{paddingLeft: leftPadding}}>
                <FixedSizeList height={400} itemCount={fields.length} itemSize={50} width={"100%"}>
                    {FieldRow}
                </FixedSizeList>
            </Box>

            {fieldEditDialogOpened &&
                companyId &&
                (isAdmin ? (
                    <FieldCreateDialog
                        companyId={companyId}
                        open={fieldEditDialogOpened}
                        mode="edit"
                        selectedField={selectedField!}
                        onClose={onFieldEditDialogClose}
                    />
                ) : (
                    <FieldEditDialog
                        companyId={companyId}
                        open={fieldEditDialogOpened}
                        selectedField={selectedField!}
                        onClose={onFieldEditDialogClose}
                    />
                ))}
        </Box>
    );
};

const getFinalColor = (field: Field): "primary" | "disabled" => {
    return field.status === FieldStatus.FINAL ? "primary" : "disabled";
};
